import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Paper, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Grid2, Typography } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { IconTrash, IconEdit, IconEye, IconDotsVertical, IconPlus, IconSearch, IconRestore } from "@tabler/icons-react";
import axios from "axios";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import StatusChip from "@components/chip/statusChip";
import ConfirmDialog from "@components/dialog/confirmDialog";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledAutocomplete from "@components/inputs/controlledAutocomplete";
import CustomIconButton from "@components/button/customIconButton";

import { useSnackbar } from "@context/snackbarContext";

import { useAuthentication } from "@redux/authentication/hook";

import { ACTION } from "@utils/constant";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";

import { applyFilters, createSearchFilter, createStatusFilter } from "@utils/filter";

const defaultQueryFields = {
  search: "",
  status: "All",
};

export default function Role() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const { user } = useAuthentication()

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      search: "",
      status: "All",
    }
  })

  const [searchQuery, setSearchQuery] = useState(defaultQueryFields)

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(false);

  const [action, setAction] = useState("")
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  const [selectedRow, setSelectedRow] = useState(null)

  const { isFetching, error, data } = useQuery({
    queryKey: ['getRole'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getRole),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
    },
  })

  // console.log(data)

  const deleteRoleMutation = useMutation({
    mutationFn: (id) => {
      return axios.delete(`${AdminApiEndpoints.deleteRole}/${id}`)
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getRole'] })
      openSnackbar("Successfully deleted role", "success");
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar("Failed to delete role", "error");
    }
  })

  const updateRoleStatusMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateRoleStatus}/${data.id}`, {status: data?.status, by: user?.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getRole'] })
      openSnackbar("Successfully updated role status", "success");
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar("Failed to update role status", "error");
    }
  })

  const handleConfirmSubmit = () => {
    if (action === ACTION.DELETE)
      deleteRoleMutation.mutate(selectedRow?.roleGuid)
    if (action === ACTION.UPDATE_STATUS)
      updateRoleStatusMutation.mutate({id: selectedRow?.roleGuid, status: !selectedRow?.status})
  }

  //================================Table================================

  const handleClickStatusChip = (row) => {
    setAction(ACTION.UPDATE_STATUS);
    setSelectedRow(row);
    setOpenConfirmDialog(true);
  }

  const handleClickDelete = () => {
    setAction(ACTION.DELETE);
    setOpenConfirmDialog(true);
    setOpenActionMenu(false);
  }

  const handleClickActions = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setOpenActionMenu(true);
    setSelectedRow(row);
  };

  const renderPopper = () => {
    return (
      <Menu
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorEl={menuAnchorEl}
        open={openActionMenu}
        onClose={()=>setOpenActionMenu(false)}
      >
        <MenuItem
          component={RouterLink}
          to={`view-role?id=${selectedRow?.roleGuid}`}
        >
          <ListItemIcon>
            <IconEye size={20}/>
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem
          component={RouterLink}
          to={`edit-role?id=${selectedRow?.roleGuid}`}
        >
          <ListItemIcon>
            <IconEdit size={20}/>
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleClickDelete}
        >
          <ListItemIcon>
            <IconTrash size={20}/>
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    );
  };

  //=====================================================================

  //================================Filter================================
  
  const handleClickSearch = (data) => {
    setSearchQuery(data)
  }

  const handleClickReset = () => {
    reset(defaultQueryFields)
    setSearchQuery(defaultQueryFields)
  }

  const filters = [
    createSearchFilter(searchQuery.search, ["roleName", "description"]),
    createStatusFilter(searchQuery.status),
  ];

  const filteredData = applyFilters(data, filters);

  //======================================================================

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <Paper
        sx={{
          p: "20px 25px",
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
        }}
      >
        <Grid2 container spacing={"20px"}>
          <Grid2 size={{ xs: 12 }}>
            <Typography
              fontSize={"0.9rem"}
              color={"#202020"}
              fontWeight={600}
            >
              Search
            </Typography>
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledTextField 
              name="search"
              control={control}
              label="Search"
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledAutocomplete 
              name="status"
              control={control}
              label="Status"
              options={["All", "Active", "Inactive"]}
            />
          </Grid2>

          <Grid2 size={12} display={'flex'} justifyContent={"flex-end"} gap={"10px"}>
            <CustomIconButton aria-label="Reset" color="#999999" onClick={handleClickReset}>
              <IconRestore />
            </CustomIconButton>
            <CustomIconButton aria-label="Search" onClick={handleSubmit(handleClickSearch)}>
              <IconSearch />
            </CustomIconButton>
          </Grid2>
        </Grid2>
      </Paper>
      <Paper
        sx={{
          p: "0px 25px",
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
        }}
      >
        <DataGrid
          loading={isFetching}
          rows={filteredData}
          localeText={{ noRowsLabel: 'No records found' }}
          getRowId={(row) => row.roleGuid}
          columns={[
            {
              field: 'roleName',
              headerName: 'Role Name',
              flex: 1, minWidth: 125,
            },
            {
              field: 'description',
              headerName: 'Description',
              flex: 2, minWidth: 150,
            },
            {
              field: 'numberOfUser',
              headerName: 'Authorized Users',
              flex: 1, minWidth: 125,
            },
            {
              field: 'status',
              headerName: 'Status',
              flex: 1, minWidth: 100,
              renderCell: (params) => {
                const statusText = params.row.status ? "Active" : "Inactive"
                return <StatusChip status={statusText} clickable onClick={() => handleClickStatusChip(params.row)}/>
              },
            },
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: (params) => (
                <IconButton aria-label="Actions" onClick={(event) => handleClickActions(event, params.row)}>
                  <IconDotsVertical />
                </IconButton>
              ),
            },
          ]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10,25,50]}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          sx={{ border: 0 }}
        />
        {renderPopper()}
      </Paper>
      
      <ConfirmDialog 
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        onSubmit={handleConfirmSubmit}
        loading={deleteRoleMutation.isPending || updateRoleStatusMutation.isPending}
        type={action === ACTION.DELETE ? "error" : "info"}
        dialogTitle={`${action} Confirmation`}
        dialogDescription={
          action === ACTION.DELETE ? 
            'Are you sure you want to delete the following role?'
          : 
            `Update the following role status to ${selectedRow?.status ? 'Inactive' : 'Active'}?`
        }
        items={[selectedRow?.roleName]}
      />
    </div>
  );
}